<script lang="ts">
	import { blogPrevNextMap, type SchemaContentMetadataDocs } from '$content';
	import SafeTime from '$lib/components/SafeTime.svelte';
	import type { z } from 'zod';
	import { authorsImagesMap } from '$assets/images/authors';
	import { createTableOfContents } from '@melt-ui/svelte';
	import TableOfContentsBase from '$lib/components/tableOfContents/TableOfContentsBase.svelte';
	import { map } from '../images';
	import slugify from 'slugify';
	import Icon from '@iconify/svelte';
	import SeoMetaTags from '$lib/components/seo/SeoMetaTags.svelte';

	export let pathname: string;
	export let metadata: z.infer<typeof SchemaContentMetadataDocs>;
	export let context: {
		title: string;
		og: {
			title: string;
			description: string;
			image: {
				url: string;
				alt: string;
				type: string;
				width: string;
				height: string;
			};
			url: string;
		};
	};

	const prev = blogPrevNextMap.prev(new URL(context.og.url).pathname.slice(1));
	const next = blogPrevNextMap.next(new URL(context.og.url).pathname.slice(1));

	const {
		elements: { item },
		states: { activeHeadingIdxs, headingsTree }
	} = createTableOfContents({
		selector: '.content',
		exclude: ['h1'],
		activeType: 'all'
	});
</script>

<svelte:head>
	<SeoMetaTags
		{context}
		title={metadata.og.title}
		description={metadata.description}
		ogTitle={metadata.og.title}
		ogDescription={metadata.og.description}
	/>
	<meta
		property="og:article:published_time"
		content={new Date(metadata.published_time).toISOString()}
	/>
</svelte:head>

<div class="pt-10 px-4 layout container mx-auto">
	<main style:grid-area="main" class="px-4 mx-auto xl:pb-20">
		<div class="breadcrumbs max-w-[calc(100vw-4rem)] prose-sm font-medium">
			<ul class="pl-0">
				<li class="pl-0">
					<a href="/blog/" class="link-hover">Blog</a>
				</li>
				<li class="pl-0">
					<a href="/" class="link-hover link-primary">{metadata.title}</a>
				</li>
			</ul>
		</div>
		<div class="mt-4 prose-sm sm:prose md:prose-lg">
			<h1
				class="vt-title"
				style:--title="title-{slugify(metadata.title).toLowerCase().replaceAll(':', '_')}"
			>
				{@html metadata.title}
			</h1>

			<p class="font-medium">{@html metadata.description}</p>

			<div
				class="vt-thumbnail"
				style:--thumbnail="thumbnail-{slugify(metadata.title).toLowerCase().replaceAll(':', '_')}"
			>
				<enhanced:img
					src={map[metadata.og.image].default}
					alt={metadata.og.title}
					sizes="(min-width: 768px) 100vw, 500px"
					class="bg-cover w-full rounded-box"
				/>
			</div>

			<div class="mt-2 mb-8 flex items-center gap-2 flex-wrap justify-between">
				<div class="flex items-center gap-1 flex-wrap">
					{#each metadata.tags as tag}
						<span class="badge badge-lg badge-info badge-outline bg-info/10 capitalize">
							{tag}
						</span>
					{/each}
				</div>
				<div class="flex items-center gap-1 flex-nowrap">
					<div class="not-prose avatar-group -space-x-3 rtl:space-x-reverse">
						{#each metadata.authors as author}
							<div class="avatar">
								<div class="w-8">
									<enhanced:img
										src={authorsImagesMap[author].default}
										alt={metadata.og.title}
										sizes="32px"
									/>
								</div>
							</div>
						{/each}
					</div>
					<div class="prose prose-sm">
						<span class="capitalize">{metadata.authors.join(', ')}</span>
						<span class="text-lg font-semibold">•</span>
						<SafeTime date={new Date(metadata.published_time)} />
					</div>
				</div>
			</div>

			<div class="content">
				<slot />
			</div>
		</div>
	</main>
	<menu
		style:grid-area="menu"
		class="xl:min-h-[calc(100vh-4rem)] max-w-prose mx-auto opacity-80 hover:opacity-100 focus-within:opacity-100 transition-opacity duration-1000 delay-150"
	>
		{#if prev || next}
			<div class="mb-4 flex items-stretch flex-wrap gap-3">
				<p class="font-semibold">You may also be interest in:</p>
				{#if prev}
					<a href={prev.path} class="block">
						<article
							class="card bg-base-100 text-base-content hover:bg-base-200 transition-colors duration-1000 card-compact"
						>
							<div class="card-body gap-4">
								<enhanced:img
									src={map[prev.glob.metadata.og.image].default}
									alt={prev.glob.metadata.og.title}
									sizes="(max-width: 1023px) 623px, 500px"
									class="bg-cover w-full rounded-box"
								/>
								<div class="prose-sm">
									<h2>{prev.glob.metadata.title}</h2>
									<p>{prev.glob.metadata.og.description}</p>
								</div>
							</div>
						</article>
					</a>
				{/if}
				{#if next}
					<a href={next.path} class="block">
						<article
							class="card bg-base-100 text-base-content hover:bg-base-200 transition-colors duration-1000 card-compact"
						>
							<div class="card-body gap-4">
								<enhanced:img
									src={map[next.glob.metadata.og.image].default}
									alt={next.glob.metadata.og.title}
									sizes="(max-width: 1023px) 623px, 500px"
									class="bg-cover w-full rounded-box"
								/>
								<div class="prose-sm">
									<h2>{next.glob.metadata.title}</h2>
									<p>{next.glob.metadata.og.description}</p>
								</div>
							</div>
						</article>
					</a>
				{/if}
			</div>
		{/if}

		<div class="xl:sticky xl:top-20">
			<div>
				<p class="font-semibold">On this page</p>
				<nav>
					<TableOfContentsBase
						tree={$headingsTree}
						activeHeadingIdxs={$activeHeadingIdxs}
						{item}
						on:click={({ detail: element }) => {
							element.scrollIntoView({ behavior: 'smooth' });
						}}
					/>
				</nav>
			</div>

			<div class="mt-20 prose-sm text-center">
				<p class="mb-0">
					Ready to focus you and your team on <strong>your product</strong>?
				</p>
				<a href="/#pricing" data-sveltekit-reload class="mt-2 btn btn-primary">
					<Icon icon="streamline:party-popper" width="20" height="20" />
					<span> Fix the Landing Page Problem - Once and for All </span>
					<Icon icon="streamline:champagne-party-alcohol" width="20" height="20" />
				</a>
			</div>
		</div>
	</menu>
	<!-- <section style:grid-area="cta" class="flex flex-col gap-4 py-8">
		<div class="flex flex-col items-center gap-y-6 px-4">
			<img src="/og.png" alt="Create landing pages" class="max-w-sm rounded-lg shadow-2xl" />
			<div class="prose mx-auto flex flex-col items-center">
				<h1 class="mb-4">Focus on <u>Your Product</u> only!</h1>
				<p class="my-3 text-center">
					Provident cupiditate voluptatem et in. Quaerat fugiat ut assumenda excepturi
					exercitationem quasi. In deleniti eaque aut repudiandae et a id nisi.
				</p>
				<button class="btn btn-primary btn-lg btn-wide">Get Started</button>
			</div>
		</div>
	</section> -->
</div>

<style lang="postcss">
	.layout {
		@apply w-screen min-h-screen grid;
		@apply grid-cols-1 auto-rows-min gap-y-20;
		/* grid-template-areas: 'main' 'cta' 'menu' 'aside'; */
		grid-template-areas: 'main' 'menu' 'aside';

		@media (min-width: 1280px) {
			grid-template-columns: minmax(65ch, 2fr) 1fr;
			/* grid-template-areas: 'main menu' 'cta cta'; */
			grid-template-areas: 'main menu';
		}
	}

	.vt-title {
		view-transition-name: var(--title);
	}

	.vt-thumbnail {
		view-transition-name: var(--thumbnail);
	}
</style>
